// Module analytics (snippets/analytics-config.liquid loads this).
//
// Markup contract:
//   [data-track-module]  a module: data-track-module-id, data-track-label,
//                        data-track-collection, and data-track-slot="collection"
//                        for collection pages (default slot is "module").
//   [data-track-item]    an item inside a module: "product" | "slide" | "video",
//                        with data-track-item-id, data-track-item-name and
//                        data-track-position.
//   [data-track-element] names a clicked element (e.g. "add_all").
//
// Events go out through Shopify.analytics.publish; the custom pixel in
// docs/analytics/custom-pixel.js forwards them to GA4 and reports attributed
// purchases from the `_wb_attr` cart attribute this file keeps in sync.
//
// Attribution is last click, per product, 7 days from the click. A product
// carries two independent slots: the module it was last clicked in, and the
// collection page it was last clicked on. A plain link click in a module (a
// banner slide, "view all") sets a session-long context that is handed to the
// products the visitor then opens or clicks, so banners get credit too.
(() => {
  const config = window.WBAnalyticsConfig;
  if (!config || window.Shopify?.designMode) return;

  const ATTRIBUTION_WINDOW = 7 * 24 * 60 * 60 * 1000;
  const CONTEXT_MAX_AGE = 30 * 60 * 1000;
  // A module counts as seen once half of it (or half the viewport, for modules
  // taller than that) has stayed on screen for a second; once per page view.
  const VIEW_RATIO = 0.5;
  const VIEW_DELAY = 1000;
  const VIDEO_MILESTONES = [25, 50, 75];
  const NAV_SELECTOR = '.swiper-button, .swiper-pagination-bullet, .product-videos__button';
  const CART_ATTRIBUTE = '_wb_attr';
  const TOUCHES_KEY = 'wb_attr_touches';
  const CONTEXT_KEY = 'wb_attr_context';
  const CART_SNAPSHOT_KEY = 'wb_attr_cart';

  const read = (store, key) => {
    try {
      return JSON.parse(store.getItem(key));
    } catch (e) {
      return null;
    }
  };
  const write = (store, key, value) => {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (e) {}
  };

  const debug = (() => {
    try {
      return localStorage.getItem('wb_analytics_debug') === '1';
    } catch (e) {
      return false;
    }
  })();

  // Shopify.analytics may not be ready on the first events; hold them until load.
  const queue = [];
  const publish = (name, data) => {
    const payload = { page_type: config.pageType };
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') payload[key] = value;
    });
    if (debug) console.info('[analytics]', name, payload);

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(name, payload);
    } else {
      queue.push([name, payload]);
    }
  };
  window.addEventListener('load', () => {
    while (queue.length && window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(...queue.shift());
  });

  const moduleData = (module) => ({
    module: module.dataset.trackModule,
    module_id: module.dataset.trackModuleId,
    module_label: module.dataset.trackLabel,
    collection: module.dataset.trackCollection,
  });

  const itemData = (item) =>
    item
      ? {
          item_type: item.dataset.trackItem,
          item_id: item.dataset.trackItemId,
          item_name: item.dataset.trackItemName,
          position: Number(item.dataset.trackPosition) || undefined,
        }
      : {};

  const findModule = (el) => el.closest('[data-track-module]');

  // ---- Attribution ----------------------------------------------------------

  const fresh = (touch, now = Date.now()) => touch && now - touch.t < ATTRIBUTION_WINDOW;

  const loadTouches = () => {
    const touches = read(localStorage, TOUCHES_KEY) || {};
    const now = Date.now();
    Object.keys(touches).forEach((id) => {
      const touch = touches[id];
      if (!fresh(touch.m, now)) delete touch.m;
      if (!fresh(touch.c, now)) delete touch.c;
      if (!touch.m && !touch.c) delete touches[id];
    });
    return touches;
  };

  const moduleTouch = (module, t = Date.now()) => ({
    n: module.dataset.trackModule,
    id: module.dataset.trackModuleId,
    l: module.dataset.trackLabel,
    t,
  });

  // Hands the session context (last plain link clicked in a module) to a
  // product, unless the product was clicked in a module more recently.
  const applyContext = (touch) => {
    const context = read(sessionStorage, CONTEXT_KEY);
    if (!context || Date.now() - context.t > CONTEXT_MAX_AGE) return false;
    if (touch.m && touch.m.t >= context.t) return false;
    touch.m = context;
    return true;
  };

  const recordTouch = (productId, module) => {
    if (!productId) return;
    const touches = loadTouches();
    const touch = touches[productId] || {};
    if (module.dataset.trackSlot === 'collection') {
      touch.c = { n: module.dataset.trackCollection, t: Date.now() };
      applyContext(touch);
    } else {
      touch.m = moduleTouch(module);
    }
    touches[productId] = touch;
    write(localStorage, TOUCHES_KEY, touches);
  };

  const recordProductView = (productId) => {
    if (!productId) return;
    const touches = loadTouches();
    const touch = touches[productId] || {};
    if (!applyContext(touch)) return;
    touches[productId] = touch;
    write(localStorage, TOUCHES_KEY, touches);
  };

  // Flat params for events and the cart attribute.
  const touchData = (touch) => ({
    module: touch?.m?.n,
    module_id: touch?.m?.id,
    module_label: touch?.m?.l,
    module_time: touch?.m?.t,
    collection: touch?.c?.n,
    collection_time: touch?.c?.t,
  });

  // ---- Cart -----------------------------------------------------------------

  const quantitiesOf = (items) =>
    items.reduce((quantities, item) => {
      quantities[item.product_id] = (quantities[item.product_id] || 0) + item.quantity;
      return quantities;
    }, {});

  let syncedAttribute = config.cart.attributes[CART_ATTRIBUTE] || '';

  // Reports attributed add-to-carts by diffing against the last cart seen in
  // this session (this covers every add path, AJAX or full page), then keeps the
  // cart attribute in step with the products' touches for the pixel.
  const handleCart = (cart) => {
    if (!Array.isArray(cart?.items)) return;
    const touches = loadTouches();
    const quantities = quantitiesOf(cart.items);
    const previous = read(sessionStorage, CART_SNAPSHOT_KEY);

    if (previous) {
      const reported = new Set();
      cart.items.forEach((item) => {
        const id = item.product_id;
        const added = quantities[id] - (previous[id] || 0);
        if (added <= 0 || reported.has(id) || !touches[id]) return;
        reported.add(id);
        const { module_time, collection_time, ...attribution } = touchData(touches[id]);
        publish('module_add_to_cart', {
          ...attribution,
          item_id: String(id),
          item_name: item.product_title,
          quantity: added,
          value: (item.final_price * added) / 100,
          currency: config.currency,
        });
      });
    }
    write(sessionStorage, CART_SNAPSHOT_KEY, quantities);

    const attribution = {};
    Object.keys(quantities).forEach((id) => {
      if (!touches[id]) return;
      const { m, c } = touches[id];
      attribution[id] = {};
      if (m) Object.assign(attribution[id], { m: m.n, mi: m.id, ml: m.l, mt: m.t });
      if (c) Object.assign(attribution[id], { c: c.n, ct: c.t });
    });
    const value = Object.keys(attribution).length ? JSON.stringify(attribution) : '';
    if (value === syncedAttribute) return;

    syncedAttribute = value;
    fetch(`${FoxTheme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [CART_ATTRIBUTE]: value } }),
    }).catch((e) => console.error('[analytics]', e));
  };

  // ---- Impressions ----------------------------------------------------------

  const seen = new Set();
  const timers = new WeakMap();
  const observed = new WeakSet();

  const viewKey = (el) => {
    const module = el.matches('[data-track-module]') ? el : findModule(el);
    const moduleKey = `${module.dataset.trackModuleId}:${module.dataset.trackModule}`;
    return el === module ? moduleKey : `${moduleKey}:${el.dataset.trackItem}:${el.dataset.trackItemId}`;
  };

  const reportView = (el) => {
    const key = viewKey(el);
    if (seen.has(key)) return;
    seen.add(key);
    observer.unobserve(el);

    if (el.matches('[data-track-module]')) {
      publish('module_view', moduleData(el));
    } else {
      publish('item_view', { ...moduleData(findModule(el)), ...itemData(el) });
    }
  };

  const isVisible = (entry) =>
    entry.isIntersecting &&
    (entry.intersectionRatio >= VIEW_RATIO || entry.intersectionRect.height >= window.innerHeight * VIEW_RATIO);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (isVisible(entry)) {
          if (!timers.has(el)) timers.set(el, setTimeout(() => reportView(el), VIEW_DELAY));
        } else if (timers.has(el)) {
          clearTimeout(timers.get(el));
          timers.delete(el);
        }
      });
    },
    { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
  );

  // Modules re-render (facets, variant changes, tabs), so rescan on DOM changes.
  const scan = () => {
    document.querySelectorAll('[data-track-module], [data-track-module] [data-track-item]').forEach((el) => {
      if (observed.has(el)) return;
      observed.add(el);
      if (!seen.has(viewKey(el))) observer.observe(el);
    });
  };

  let scanScheduled = false;
  new MutationObserver(() => {
    if (scanScheduled) return;
    scanScheduled = true;
    requestAnimationFrame(() => {
      scanScheduled = false;
      scan();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // ---- Clicks ---------------------------------------------------------------

  const elementName = (target) => {
    if (target.dataset.trackElement) return target.dataset.trackElement;
    if (target.closest('form[action*="/cart/add"]') || target.name === 'add') return 'add_to_cart';
    return target.matches('a') ? 'link' : 'button';
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target.closest('a[href], button, [role="button"]');
      if (!target || target.matches(NAV_SELECTOR)) return;
      const module = findModule(target);
      if (!module) return;

      const item = target.closest('[data-track-item]');
      const element = elementName(target);
      publish('module_click', { ...moduleData(module), ...itemData(item), element });

      if (item?.dataset.trackItem === 'product') {
        recordTouch(item.dataset.trackItemId, module);
      } else if (element === 'add_all') {
        // Bundle "Add all to cart": credit every selected product.
        module.querySelector('product-portfolio')?.selectedItems?.forEach((selected) => {
          recordTouch(selected.dataset.productId, module);
        });
      } else if (target.matches('a') && module.dataset.trackSlot !== 'collection') {
        write(sessionStorage, CONTEXT_KEY, moduleTouch(module));
      }
    },
    true
  );

  // ---- Videos ---------------------------------------------------------------

  // Media events don't bubble, so listen in the capture phase.
  const videoProgress = new WeakMap();
  const videoEvent = (name, video, extra = {}) => {
    const item = video.closest('[data-track-item="video"]');
    const module = item && findModule(item);
    if (!module) return;
    publish(name, { ...moduleData(module), ...itemData(item), ...extra });
  };

  document.addEventListener(
    'play',
    (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || videoProgress.has(video)) return;
      videoProgress.set(video, new Set());
      videoEvent('video_start', video);
    },
    true
  );

  document.addEventListener(
    'timeupdate',
    (event) => {
      const video = event.target;
      const reached = videoProgress.get(video);
      if (!reached || !video.duration) return;
      const percent = (video.currentTime / video.duration) * 100;
      VIDEO_MILESTONES.forEach((milestone) => {
        if (percent < milestone || reached.has(milestone)) return;
        reached.add(milestone);
        videoEvent('video_progress', video, { video_percent: milestone });
      });
    },
    true
  );

  document.addEventListener(
    'ended',
    (event) => {
      if (videoProgress.has(event.target)) videoEvent('video_complete', event.target);
    },
    true
  );

  // ---- Init -----------------------------------------------------------------

  scan();
  recordProductView(config.productId);
  handleCart(config.cart);
  FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => handleCart(data?.cart));
})();
